import "video.js/dist/video-js.css";

import "../../../videojs-style-overrides.css";

import * as React from "react";
import videojs, { Player } from "video.js";

import { VideoJsOptions } from "./types";
import {
  Marker,
  MarkerConfig,
  progressMarkerPlugin,
} from "./videojs-marker-plugin";

let hasRegisteredProgressMarkerPlugin = false;
function ensureProgressMarkerPluginRegistered() {
  if (hasRegisteredProgressMarkerPlugin) {
    return;
  }

  // `registerPlugin` will warn/throw if we attempt to register twice.
  videojs.registerPlugin("progressMarker", progressMarkerPlugin);
  hasRegisteredProgressMarkerPlugin = true;
}

export interface VideoJsWrapperProps {
  options: VideoJsOptions;
  className?: string;
  onReady?: (player: Player) => void;
  markers?: Marker[];
  markerConfig?: MarkerConfig;
}

export default function VideoJsWrapper({
  options,
  className,
  onReady,
  markers,
  markerConfig,
}: VideoJsWrapperProps) {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const playerRef = React.useRef<Player | null>(null);

  React.useEffect(() => {
    if (!containerRef.current) {
      return;
    }

    // Make sure Video.js player is only initialized once.
    if (!playerRef.current) {
      // The Video.js player needs to be _inside_ the component el for React 18 Strict Mode.
      const videoElement = document.createElement("video-js");
      videoElement.classList.add("vjs-big-play-centered");
      containerRef.current.appendChild(videoElement);

      ensureProgressMarkerPluginRegistered();

      const player = videojs(videoElement, options, () => {
        onReady?.(player as Player);
        player.on("contextmenu", (event: MouseEvent) => {
          event.preventDefault();
          event.stopPropagation();
        });
      }) as Player;
      playerRef.current = player;

      if (markers?.length) {
        player.progressMarker(markers, markerConfig);
      }
    } else {
      const player = playerRef.current;
      player.autoplay(options.autoplay);
      if (options.sources) {
        player.src(options.sources);
      }
    }
  }, [markerConfig, markers, onReady, options]);

  // Dispose the Video.js player when the component unmounts.
  React.useEffect(() => {
    return () => {
      const player = playerRef.current;
      if (player && !player.isDisposed()) {
        player.dispose();
        playerRef.current = null;
      }
    };
  }, []);

  return (
    <div data-vjs-player className={className}>
      <div ref={containerRef} />
    </div>
  );
}
